/* eslint-disable */
import { useState, useCallback, useEffect } from "react";
import {
  collection, query, orderBy, limit, getDocs,
  doc, writeBatch, where,
} from "firebase/firestore";
import { formatDistanceToNow } from "date-fns";
import { auth } from "../firebase";
import { takeSnapshot } from "../utils/snapshots";
import Toast, { useToast } from "./Toast";
import { Icon } from "../utils/icons";
import { COLOR, FONT, RADIUS } from "../utils/theme";
import useWindowWidth from "../utils/useWindowWidth";

export default function VersionHistory({ tripId, db, days, activities, onRestore, onClose }) {
  const [closing, setClosing] = useState(false);
  const { isDesktop } = useWindowWidth();
  const [toast, showToast] = useToast();

  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  // ── Fetch snapshots on mount ────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const q = query(
          collection(db, "trips", tripId, "snapshots"),
          orderBy("takenAt", "desc"),
          limit(50)
        );
        const snap = await getDocs(q);
        if (!cancelled) {
          setSnapshots(snap.docs.map(d => ({ id: d.id, ...d.data() })));
          setLoading(false);
        }
      } catch (err) {
        console.error("[VersionHistory] fetch failed:", err);
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [db, tripId]);

  // ── Dismiss ─────────────────────────────────────────────────────────────
  const dismiss = useCallback(() => {
    setClosing(true);
    setTimeout(onClose, 260);
  }, [onClose]);

  // ── Restore ─────────────────────────────────────────────────────────────
  const handleRestore = useCallback(async (snapshot) => {
    if (!snapshot.days?.length || !snapshot.activities?.length) {
      showToast("Snapshot data missing — cannot restore.");
      return;
    }

    setRestoringId(snapshot.id);

    takeSnapshot(db, tripId, "Before restore", days, activities).catch(console.error);

    try {
      // Fetch authoritative activity list if prop is empty/partial
      let currentActivities = activities;
      if (!currentActivities?.length) {
        const snap = await getDocs(
          query(collection(db, "activities"), where("ownerUid", "==", auth.currentUser.uid), where("tripId", "==", tripId))
        );
        currentActivities = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      }

      const batch = writeBatch(db);

      // Set every day from the snapshot (inject ownerUid for old snapshots)
      const uid = auth.currentUser.uid;
      snapshot.days.forEach(day => {
        batch.set(doc(db, "days", day.id), { ...day, ownerUid: uid }, { merge: true });
      });

      // Set every activity from the snapshot (inject ownerUid for old snapshots)
      snapshot.activities.forEach(act => {
        batch.set(doc(db, "activities", act.id), { ...act, ownerUid: uid });
      });

      // Delete days that exist now but are NOT in the snapshot
      const snapshotDayIds = new Set(snapshot.days.map(d => d.id));
      days.forEach(d => {
        if (!snapshotDayIds.has(d.id)) {
          batch.delete(doc(db, "days", d.id));
        }
      });

      // Delete activities that exist now but are NOT in the snapshot
      const snapshotActIds = new Set(snapshot.activities.map(a => a.id));
      currentActivities.forEach(a => {
        if (!snapshotActIds.has(a.id)) {
          batch.delete(doc(db, "activities", a.id));
        }
      });

      await batch.commit();
      onRestore(snapshot.days, snapshot.activities);
      showToast(`Restored to: ${snapshot.label}`);
      setClosing(true);
      setTimeout(onClose, 260);
    } catch (err) {
      console.error("[VersionHistory] restore failed:", err);
      showToast("Restore failed — please try again.");
      setRestoringId(null);
    }
  }, [db, tripId, days, activities, onRestore, onClose, showToast]);

  // ── Visible list ────────────────────────────────────────────────────────
  const visible = showAll ? snapshots : snapshots.slice(0, 5);

  return (
    <>
      <Toast message={toast.message} type={toast.type} visible={toast.visible} />
      <div
        onClick={dismiss}
        style={{
          position: "fixed", inset: 0, zIndex: 200,
          display: "flex",
          alignItems: isDesktop ? "stretch" : "flex-end",
          justifyContent: isDesktop ? "flex-end" : undefined,
          flexDirection: isDesktop ? "row" : "column",
          background: closing ? "rgba(44,24,16,0)" : "rgba(44,24,16,0.45)",
          transition: "background 0.26s",
        }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: "relative",
            background: COLOR.cream,
            borderRadius: isDesktop ? 0 : "24px 24px 0 0",
            width: isDesktop ? 420 : "100%",
            maxWidth: isDesktop ? 420 : 680,
            height: isDesktop ? "100vh" : undefined,
            maxHeight: isDesktop ? "100vh" : "88vh",
            overflowY: "auto",
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
            paddingBottom: isDesktop ? 24 : "max(16px, env(safe-area-inset-bottom))",
            animation: isDesktop
              ? `${closing ? "slideOutRight" : "slideInRight"} 0.26s cubic-bezier(0.32,0.72,0,1) forwards`
              : `${closing ? "slideDown" : "slideUp"} 0.26s cubic-bezier(0.32,0.72,0,1) forwards`,
          }}
        >
          {/* Header */}
          <div style={{
            position: "sticky", top: 0, zIndex: 10,
            background: COLOR.cream,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: `1px solid ${COLOR.borderWarm}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Icon name="snapshot" size={18} color={COLOR.inkMid} />
              <span style={{
                fontSize: 16, fontWeight: 600,
                fontFamily: FONT.serif, color: COLOR.inkDark,
              }}>
                Version History
              </span>
            </div>
            <button
              onClick={dismiss}
              style={{
                background: "none", border: "none",
                cursor: "pointer", padding: 4,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <Icon name="close" size={20} color={COLOR.inkMid} />
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: "12px 20px" }}>
            {loading && (
              <div style={{
                textAlign: "center", padding: "40px 0",
                fontSize: 13, fontFamily: FONT.mono, color: COLOR.inkMid,
              }}>
                Loading history…
              </div>
            )}

            {error && !loading && (
              <div style={{
                textAlign: "center", padding: "40px 0",
                fontSize: 13, fontFamily: FONT.mono, color: COLOR.inkMid,
              }}>
                Couldn't load history.
              </div>
            )}

            {!loading && !error && snapshots.length === 0 && (
              <div style={{
                textAlign: "center", padding: "40px 0",
                fontSize: 13, fontFamily: FONT.mono, color: COLOR.inkMid,
              }}>
                No version history yet.
              </div>
            )}

            {!loading && !error && snapshots.length > 0 && (
              <>
                {visible.map(snap => (
                  <button
                    key={snap.id}
                    data-testid="snapshot-entry"
                    onClick={() => handleRestore(snap)}
                    disabled={restoringId !== null}
                    style={{
                      display: "flex", alignItems: "center", gap: 12,
                      width: "100%", minHeight: 56,
                      padding: "10px 12px",
                      background: restoringId === snap.id ? COLOR.warmSurface : "transparent",
                      border: "none", borderBottom: `1px solid ${COLOR.borderWarm}`,
                      cursor: restoringId !== null ? "default" : "pointer",
                      borderRadius: RADIUS.md,
                      transition: "background 0.15s",
                      textAlign: "left",
                      opacity: restoringId !== null && restoringId !== snap.id ? 0.5 : 1,
                    }}
                    onMouseEnter={e => {
                      if (!restoringId) e.currentTarget.style.background = COLOR.warmSurface;
                    }}
                    onMouseLeave={e => {
                      if (restoringId !== snap.id) e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <Icon name="unbook" size={16} color={COLOR.inkMid} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 14, fontWeight: 600,
                        fontFamily: FONT.serif, color: COLOR.inkDark,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {snap.label}
                      </div>
                      <div style={{
                        fontSize: 12, fontFamily: FONT.mono, color: COLOR.inkMid,
                        marginTop: 2,
                      }}>
                        {snap.takenAt
                          ? formatDistanceToNow(snap.takenAt.toDate(), { addSuffix: true })
                          : "just now"}
                      </div>
                    </div>
                  </button>
                ))}

                {!showAll && snapshots.length > 5 && (
                  <button
                    onClick={() => setShowAll(true)}
                    style={{
                      background: "none", border: "none",
                      cursor: "pointer", padding: "14px 0",
                      fontSize: 13, fontFamily: FONT.mono, color: COLOR.inkMid,
                      width: "100%", textAlign: "center",
                    }}
                  >
                    Show all {snapshots.length} versions
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
